// Land contours (lane C, backlog C-3; territory-visuals.md §1): marching squares over the cell
// centres, so a territory's edge runs through the midpoints between an owned and an unowned
// cell centre. Axis-aligned edges sit exactly on the cell boundary, a 45° staircase becomes one
// straight line, and steps on other angles stay ≤ 0.35 cell: no 27 px staircase.
//
// Output: for one owner and one rectangle of the grid, a list of convex polygons (one per
// marching-squares square, full squares merged into horizontal runs) that the land layer adds
// to ONE path and fills once, so neighbouring pieces union without seams. Saddles (cases 5 and
// 10) join the two owned corners (the territory stays connected across a diagonal). Pure.

/** A flat list of polygons, each `[x0, y0, x1, y1, …]` in cells (world units). */
export type Polygons = number[][];

/**
 * Polygons covering owner `id` over the squares whose corners are the cell centres
 * `sx0 … sx1` × `sy0 … sy1` (sample columns and rows, inclusive). `at(cx, cy)` reads the owner of
 * a cell (0 outside the grid).
 */
export function contourPolygons(
  at: (cx: number, cy: number) => number,
  id: number,
  sx0: number,
  sy0: number,
  sx1: number,
  sy1: number,
): Polygons {
  const out: Polygons = [];
  for (let y = sy0; y < sy1; y++) {
    const top = y + 0.5;
    const bot = y + 1.5;
    let runStart = -1;
    for (let x = sx0; x < sx1; x++) {
      const tl = at(x, y) === id ? 1 : 0;
      const tr = at(x + 1, y) === id ? 1 : 0;
      const br = at(x + 1, y + 1) === id ? 1 : 0;
      const bl = at(x, y + 1) === id ? 1 : 0;
      const c = (tl << 3) | (tr << 2) | (br << 1) | bl;
      if (c === 15) {
        if (runStart < 0) runStart = x;
        continue;
      }
      if (runStart >= 0) {
        out.push([runStart + 0.5, top, x + 0.5, top, x + 0.5, bot, runStart + 0.5, bot]);
        runStart = -1;
      }
      if (c !== 0) pushCase(out, c, x + 0.5, top);
    }
    if (runStart >= 0)
      out.push([runStart + 0.5, top, sx1 + 0.5, top, sx1 + 0.5, bot, runStart + 0.5, bot]);
  }
  return out;
}

/**
 * One square's polygon for case `c` (bits tl 8, tr 4, br 2, bl 1), square top-left at (x, y),
 * side 1. Edge midpoints: top (x+½, y), right (x+1, y+½), bottom (x+½, y+1), left (x, y+½).
 */
function pushCase(out: Polygons, c: number, x: number, y: number): void {
  const tlx = x;
  const tly = y;
  const trx = x + 1;
  const bry = y + 1;
  const mx = x + 0.5;
  const my = y + 0.5;
  switch (c) {
    case 1: // bl
      out.push([tlx, my, mx, bry, tlx, bry]);
      return;
    case 2: // br
      out.push([mx, bry, trx, my, trx, bry]);
      return;
    case 3: // bl br
      out.push([tlx, my, trx, my, trx, bry, tlx, bry]);
      return;
    case 4: // tr
      out.push([mx, tly, trx, tly, trx, my]);
      return;
    case 5: // tr bl (saddle, joined)
      out.push([mx, tly, trx, tly, trx, my, mx, bry, tlx, bry, tlx, my]);
      return;
    case 6: // tr br
      out.push([mx, tly, trx, tly, trx, bry, mx, bry]);
      return;
    case 7: // tr br bl
      out.push([mx, tly, trx, tly, trx, bry, tlx, bry, tlx, my]);
      return;
    case 8: // tl
      out.push([tlx, tly, mx, tly, tlx, my]);
      return;
    case 9: // tl bl
      out.push([tlx, tly, mx, tly, mx, bry, tlx, bry]);
      return;
    case 10: // tl br (saddle, joined)
      out.push([tlx, tly, mx, tly, trx, my, trx, bry, mx, bry, tlx, my]);
      return;
    case 11: // tl br bl
      out.push([tlx, tly, mx, tly, trx, my, trx, bry, tlx, bry]);
      return;
    case 12: // tl tr
      out.push([tlx, tly, trx, tly, trx, my, tlx, my]);
      return;
    case 13: // tl tr bl
      out.push([tlx, tly, trx, tly, trx, my, mx, bry, tlx, bry]);
      return;
    case 14: // tl tr br
      out.push([tlx, tly, trx, tly, trx, bry, mx, bry, tlx, my]);
      return;
    default:
      return;
  }
}

/** Twice the signed area of a polygon (positive = clockwise on a +y-down screen). */
export function polygonArea2(p: readonly number[]): number {
  let a = 0;
  const n = p.length / 2;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    a +=
      (p[i * 2] as number) * (p[j * 2 + 1] as number) -
      (p[j * 2] as number) * (p[i * 2 + 1] as number);
  }
  return a;
}

/** The total area (cells²) the polygons cover (they never overlap). */
export function polygonsArea(polys: Polygons): number {
  let a = 0;
  for (const p of polys) a += Math.abs(polygonArea2(p)) / 2;
  return a;
}
